import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  AppBar,
  Toolbar,
  IconButton,
  Card,
  CardContent,
  CardActionArea,
  TextField,
  InputAdornment,
  Avatar,
  Stack,
  Chip,
  CircularProgress,
  Menu,
  MenuItem,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { apiGet } from "../utils/apiClient";
import { useAuth } from "../contexts/AuthContext";
import { HEADER_GRADIENT, HEADER_TEXT } from "../App";

// ── Shared admin app bar ──────────────────────────────────────────────────────
function AdminAppBar({ title, onBack }) {
  const navigate = useNavigate();
  const { user, setAdminMode } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleExitAdmin = () => {
    setAnchorEl(null);
    setAdminMode(false);
    navigate("/");
  };

  return (
    <AppBar position="static" elevation={0} sx={{ background: HEADER_GRADIENT }}>
      <Toolbar>
        {onBack && (
          <IconButton edge="start" onClick={onBack} sx={{ mr: 1 }}>
            <ArrowBackIcon sx={{ color: HEADER_TEXT }} />
          </IconButton>
        )}
        <Typography variant="h6" sx={{ flex: 1, color: HEADER_TEXT, fontWeight: 700 }}>{title}</Typography>
        <Chip label="Admin" size="small"
          sx={{ bgcolor: "rgba(255,100,100,0.25)", color: "#7a0000", fontWeight: 700, mr: 1 }} />
        <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ p: 0.5 }}>
          {user?.avatar_url
            ? <Avatar src={user.avatar_url} sx={{ width: 36, height: 36, border: "2px solid rgba(255,255,255,0.8)" }} />
            : <AccountCircleIcon sx={{ color: HEADER_TEXT }} />
          }
        </IconButton>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
          <MenuItem disabled><Typography variant="body2">{user?.name}</Typography></MenuItem>
          <MenuItem onClick={handleExitAdmin}>Exit Admin Mode</MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}

// ── Admin Dashboard ───────────────────────────────────────────────────────────
function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    apiGet("/api/admin/stats").then(setStats).catch(() => {});
  }, []);

  const loading = stats === null;

  const SubStat = ({ label, value }) => (
    <Box>
      <Typography variant="caption" color="text.disabled" sx={{ display: "block", lineHeight: 1.3 }}>{label}</Typography>
      <Typography variant="body2" fontWeight={700}>{loading ? "—" : value}</Typography>
    </Box>
  );

  const fmt = (n) => parseFloat(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 1 });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100dvh" }}>
      <AdminAppBar title="✈️ TravelSplit" />
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        <Container maxWidth="sm" sx={{ py: 3 }}>
          <Typography variant="caption" fontWeight={700} color="text.secondary"
            sx={{ textTransform: "uppercase", letterSpacing: 1, display: "block", mb: 2 }}>
            Dashboard
          </Typography>
          <Stack spacing={2}>

            {/* Trips */}
            <Card>
              <CardActionArea onClick={() => navigate("/admin/trips")} sx={{ borderRadius: 24 }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h2" fontWeight={800} color="text.primary" sx={{ lineHeight: 1.1 }}>
                    {loading ? <CircularProgress size={36} /> : stats.trip_count}
                  </Typography>
                  <Typography fontWeight={700} color="text.secondary" sx={{ mt: 0.25, mb: 2 }}>Trips</Typography>
                  <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                    <SubStat label="New this month" value={stats?.trips_this_month} />
                    <SubStat label="Avg travelers" value={fmt(stats?.avg_travelers_per_trip ?? 0)} />
                    <SubStat label="Avg expenses/trip" value={fmt(stats?.avg_expenses_per_trip ?? 0)} />
                    <SubStat label="Expenses logged" value={stats?.total_expenses?.toLocaleString()} />
                    <SubStat label="Total spent*" value={parseFloat(stats?.total_spent ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} />
                  </Box>
                  <Typography variant="caption" color="text.disabled" sx={{ mt: 1.5, display: "block" }}>
                    * sum across all currencies
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>

            {/* Users */}
            <Card>
              <CardActionArea onClick={() => navigate("/admin/users")} sx={{ borderRadius: 24 }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h2" fontWeight={800} color="text.primary" sx={{ lineHeight: 1.1 }}>
                    {loading ? <CircularProgress size={36} /> : stats.user_count}
                  </Typography>
                  <Typography fontWeight={700} color="text.secondary" sx={{ mt: 0.25, mb: 2 }}>Users</Typography>
                  <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                    <SubStat label="New this month" value={stats?.users_this_month} />
                    <SubStat label="Active (have trips)" value={stats?.active_users} />
                    <SubStat label="Avg trips/user" value={fmt(stats?.avg_trips_per_user ?? 0)} />
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>

            {/* Pending approvals */}
            <Card>
              <CardActionArea onClick={() => navigate("/admin/pending")} sx={{ borderRadius: 24 }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h2" fontWeight={800}
                    sx={{ lineHeight: 1.1, color: stats?.pending_approvals > 0 ? "#e17055" : "text.primary" }}>
                    {loading ? <CircularProgress size={36} /> : stats.pending_approvals}
                  </Typography>
                  <Typography fontWeight={700} color="text.secondary" sx={{ mt: 0.25 }}>Pending Approvals</Typography>
                  <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: "block" }}>
                    Join requests awaiting approval
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>

          </Stack>
        </Container>
      </Box>
    </Box>
  );
}

// ── Admin Trips ───────────────────────────────────────────────────────────────
function AdminTrips() {
  const navigate = useNavigate();
  const [trips, setTrips] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiGet("/api/trips?admin=true")
      .then(setTrips)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = trips.filter((t) =>
    !search || t.name.toLowerCase().includes(search.toLowerCase()) || t.owner_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100dvh" }}>
      <AdminAppBar title="All Trips" onBack={() => navigate("/admin")} />
      <Box sx={{ px: 2, pt: 1.5, pb: 1, flexShrink: 0 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by trip name or owner…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "text.disabled" }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        <Container maxWidth="sm" sx={{ py: 2.5, pb: 4 }}>
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
              <CircularProgress />
            </Box>
          )}
          {!loading && filtered.length === 0 && (
            <Typography color="text.secondary" textAlign="center" sx={{ mt: 6 }}>No trips found.</Typography>
          )}
          {!loading && filtered.map((trip) => (
            <Card key={trip.id} sx={{ mb: 1.5 }}>
              <CardActionArea onClick={() => navigate(`/trips/${trip.id}`)} sx={{ borderRadius: 24 }}>
                <CardContent sx={{ p: 3, pb: "20px !important" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <Typography fontWeight={700} fontSize="1.05rem" noWrap color="text.primary" sx={{ flex: 1, mr: 1 }}>
                      {trip.name}
                    </Typography>
                    {parseFloat(trip.total_spent) > 0 && (
                      <Typography fontWeight={800} fontSize="1.05rem" color="#2D3436" sx={{ whiteSpace: "nowrap" }}>
                        {parseFloat(trip.total_spent).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {trip.currency}
                      </Typography>
                    )}
                  </Box>
                  {trip.owner_name && (
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ mt: 0.25, display: "block" }}>
                      by {trip.owner_name}
                    </Typography>
                  )}
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      label={`${trip.traveler_count} ${Number(trip.traveler_count) === 1 ? "Traveler" : "Travelers"}`}
                      size="small"
                      sx={{ bgcolor: "rgba(168,216,234,0.3)", color: "text.secondary", height: 24, fontSize: "0.7rem" }}
                    />
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Container>
      </Box>
    </Box>
  );
}

// ── User list ─────────────────────────────────────────────────────────────────
function UserList() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (q = "") => {
    setLoading(true);
    try {
      const data = await apiGet(`/api/admin/users${q ? `?search=${encodeURIComponent(q)}` : ""}`);
      setUsers(data);
    } catch {
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const t = setTimeout(() => load(search), 300);
    return () => clearTimeout(t);
  }, [search, load]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100dvh" }}>
      <AdminAppBar title="Users" onBack={() => navigate("/admin")} />

      <Box sx={{ px: 2, pt: 1.5, pb: 1, flexShrink: 0 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by name or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "text.disabled" }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto" }}>
        <Container maxWidth="sm" sx={{ pb: 4 }}>
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
              <CircularProgress />
            </Box>
          )}
          {!loading && users.length === 0 && (
            <Typography color="text.secondary" textAlign="center" sx={{ mt: 6 }}>
              No users found.
            </Typography>
          )}
          {!loading && (
            <Stack spacing={1}>
              {users.map((u) => (
                <Card key={u.id} variant="outlined">
                  <CardActionArea onClick={() => navigate(`/admin/users/${u.id}`)} sx={{ borderRadius: 24 }}>
                    <CardContent sx={{ pb: "12px !important" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Avatar src={u.avatar_url} sx={{ width: 40, height: 40 }}>
                          {u.name?.[0]}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography fontWeight={700} noWrap>{u.name}</Typography>
                          <Typography variant="caption" color="text.secondary" noWrap display="block">
                            {u.email}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                          <Typography variant="body2" fontWeight={700}>
                            {u.trip_count} {Number(u.trip_count) === 1 ? "trip" : "trips"}
                          </Typography>
                          <Typography variant="caption" color="text.disabled">
                            joined {new Date(u.created_at).toLocaleDateString()}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              ))}
            </Stack>
          )}
        </Container>
      </Box>
    </Box>
  );
}

// ── User's trips ──────────────────────────────────────────────────────────────
function UserTrips() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [trips, setTrips] = useState([]);
  const [userName, setUserName] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      apiGet(`/api/admin/users/${userId}/trips`),
      apiGet(`/api/admin/users`).then((users) => users.find((u) => String(u.id) === String(userId))),
    ]).then(([tripsData, user]) => {
      setTrips(tripsData);
      setUserName(user?.name || "User");
    }).catch(() => {}).finally(() => setLoading(false));
  }, [userId]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100dvh" }}>
      <AdminAppBar
        title={<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><PersonIcon sx={{ fontSize: "1.2rem" }} />{userName}</Box>}
        onBack={() => navigate("/admin/users")}
      />

      <Box sx={{ flex: 1, overflowY: "auto" }}>
        <Container maxWidth="sm" sx={{ py: 2.5 }}>
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
              <CircularProgress />
            </Box>
          )}
          {!loading && trips.length === 0 && (
            <Typography color="text.secondary" textAlign="center" sx={{ mt: 6 }}>
              No trips yet.
            </Typography>
          )}
          {!loading && trips.length > 0 && (
            <Stack spacing={1.5}>
              {trips.map((trip) => (
                <Card key={trip.id} variant="outlined">
                  <CardActionArea onClick={() => navigate(`/trips/${trip.id}`)} sx={{ borderRadius: 24 }}>
                    <CardContent sx={{ pb: "16px !important" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <Typography fontWeight={700} fontSize="1.05rem" sx={{ flex: 1, mr: 1 }} noWrap>
                          {trip.name}
                        </Typography>
                        {parseFloat(trip.total_spent) > 0 && (
                          <Typography fontWeight={800} fontSize="1.05rem" sx={{ whiteSpace: "nowrap" }}>
                            {parseFloat(trip.total_spent).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {trip.currency}
                          </Typography>
                        )}
                      </Box>
                      {trip.description && (
                        <Typography variant="body2" color="text.secondary" noWrap sx={{ mt: 0.25 }}>
                          {trip.description}
                        </Typography>
                      )}
                      <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                        <Chip
                          label={`${trip.traveler_count} ${Number(trip.traveler_count) === 1 ? "Traveler" : "Travelers"}`}
                          size="small"
                          sx={{ bgcolor: "rgba(168,216,234,0.3)", color: "text.secondary", height: 24, fontSize: "0.7rem" }}
                        />
                        <Chip
                          label={trip.role === "owner" ? "Owner" : "Member"}
                          size="small"
                          sx={{ bgcolor: trip.role === "owner" ? "rgba(168,216,234,0.5)" : "rgba(252,227,138,0.5)", color: "#2D3436", height: 24, fontSize: "0.7rem", fontWeight: 700 }}
                        />
                        <Typography variant="caption" color="text.disabled" sx={{ ml: "auto" }}>
                          {new Date(trip.created_at).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              ))}
            </Stack>
          )}
        </Container>
      </Box>
    </Box>
  );
}

// ── Pending Approvals ─────────────────────────────────────────────────────────
function AdminPending() {
  const navigate = useNavigate();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiGet("/api/admin/pending")
      .then(setTrips)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100dvh" }}>
      <AdminAppBar title="Pending Approvals" onBack={() => navigate("/admin")} />
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        <Container maxWidth="sm" sx={{ py: 2.5, pb: 4 }}>
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
              <CircularProgress />
            </Box>
          )}
          {!loading && trips.length === 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mt: 6, gap: 1 }}>
              <Typography fontSize="2.5rem">🎉</Typography>
              <Typography color="text.secondary" textAlign="center">No pending approvals!</Typography>
            </Box>
          )}
          {!loading && trips.map((trip) => (
            <Card key={trip.id} sx={{ mb: 2 }}>
              <CardActionArea onClick={() => navigate(`/trips/${trip.id}`)} sx={{ borderRadius: 24 }}>
                <CardContent sx={{ p: 3, pb: "20px !important" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.5 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography fontWeight={700} fontSize="1.05rem" noWrap color="text.primary">
                        {trip.name}
                      </Typography>
                      {trip.owner_name && (
                        <Typography variant="caption" color="text.secondary">by {trip.owner_name}</Typography>
                      )}
                    </Box>
                    <Chip
                      label={`${trip.pending_count} waiting`}
                      size="small"
                      sx={{ bgcolor: "rgba(255,118,117,0.15)", color: "#e17055", fontWeight: 700, height: 24, fontSize: "0.7rem", ml: 1, flexShrink: 0 }}
                    />
                  </Box>
                  <Stack spacing={1}>
                    {trip.pending_members.map((m) => (
                      <Box key={m.user_id} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Avatar src={m.avatar_url} sx={{ width: 32, height: 32, fontSize: "0.85rem" }}>
                          {m.name?.[0]}
                        </Avatar>
                        <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>{m.name}</Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <AccessTimeIcon sx={{ fontSize: "0.75rem", color: "text.disabled" }} />
                          <Typography variant="caption" color="text.disabled">
                            {new Date(m.requested_at).toLocaleDateString()}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Container>
      </Box>
    </Box>
  );
}

export { AdminDashboard, AdminTrips, AdminPending, UserList, UserTrips };
